import useGetEstimatePreview from '@/api/queries/estimates/useGetEstimatePreview';
import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import useGetProjectEstimate from '@/api/queries/project/useGetProjectEstimate';
import { Badge, EmptyState, UntitledLoading } from '@payaca/ambrosia';
import LoaderOverlay from '@payaca/components/loaderOverlay/LoaderOverlay';
import ShadowDomRenderer from '@payaca/components/shadowDomRenderer/ShadowDomRenderer';
import { FC } from 'react';
import useLivePreview from '../useLivePreview';

type EstimatePreviewPaneProps = {
  projectId: string;
  estimateId: string;
  // Called with the `data-tab-target` of the region the user clicked, so the
  // preview can drive the active section tab.
  onSelectTab?: (target: string) => void;
  // The active `data-tab-target`; its region is scrolled into view on change.
  activeTarget?: string | null;
};

const EstimatePreviewPane: FC<EstimatePreviewPaneProps> = (props) => {
  const { projectId, estimateId, onSelectTab, activeTarget } = props;

  // A sent Estimate carries the document HTML frozen at send time. It's loaded
  // with the rest of the estimate (no separate request), so the snapshot can
  // render immediately — before the always-loading live preview resolves.
  const { estimate } = useGetProjectEstimate(projectId, estimateId);
  const sentDocumentHtml = estimate?.sentDocumentHtml ?? null;

  const { preview, isLoading, isError, isOutOfSync } = useGetEstimatePreview(
    sentDocumentHtml ? undefined : estimateId
  );

  // Render the snapshot — exactly what the Customer received — rather than
  // re-rendering against a since-changed template or data. The live preview
  // below is only reached for a DRAFT (or an ARCHIVED Estimate never sent).
  if (sentDocumentHtml !== null) {
    return (
      <ShadowDomRenderer
        html={sentDocumentHtml}
        data-testid="estimate-document-preview"
        className="pointer-events-none overflow-hidden rounded-lg bg-white shadow-md border"
      />
    );
  }

  if (isLoading) {
    return (
      <div className="relative min-h-96 rounded-md bg-white shadow-md">
        <LoaderOverlay />
      </div>
    );
  }

  if (isError || !preview) {
    return (
      <div className="flex min-h-96 items-center justify-center rounded-md bg-white shadow-md">
        <EmptyState text="Preview unavailable" />
      </div>
    );
  }

  return (
    <LiveEstimatePreview
      template={preview.template}
      context={preview.context}
      isOutOfSync={isOutOfSync}
      pendingSolarLayoutImage={preview.pendingSolarLayoutImage}
      onSelectTab={onSelectTab}
      activeTarget={activeTarget}
    />
  );
};

type LiveEstimatePreviewProps = {
  template: string;
  context: unknown;
  isOutOfSync: boolean;
  pendingSolarLayoutImage: boolean;
  onSelectTab?: (target: string) => void;
  activeTarget?: string | null;
};

const LiveEstimatePreview: FC<LiveEstimatePreviewProps> = (props) => {
  const {
    template,
    context,
    isOutOfSync,
    pendingSolarLayoutImage,
    onSelectTab,
    activeTarget,
  } = props;
  const { data: regionPreferences } = useGetMyRegionPreferences();
  const hostRef = useLivePreview({
    template: template ?? '',
    context: context,
    locale: regionPreferences?.locale,
    timezone: regionPreferences?.timezone,
    onTabTargetClick: onSelectTab,
    activeTarget,
  });

  return (
    <div className="relative">
      <div
        ref={hostRef}
        data-testid="estimate-document-preview"
        className="overflow-hidden rounded-lg bg-white shadow-md border"
      />
      {(isOutOfSync || pendingSolarLayoutImage) && (
        <div
          className="pointer-events-none absolute left-3 top-3"
          role="status"
        >
          <Badge
            variant="white"
            size="sm"
            className="flex items-center gap-1.5"
          >
            <UntitledLoading className="size-3" />
            {pendingSolarLayoutImage ? 'Preparing roof image…' : 'Syncing…'}
          </Badge>
        </div>
      )}
    </div>
  );
};

export default EstimatePreviewPane;
